import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { X, Check, ChartLineUp, ClockCounterClockwise } from "@phosphor-icons/react";

/**
 * LogSiloTonsModal — Enter the conveyor scale reading.
 *
 * Mirrors the RBTG "Log Hatch Tons" UX exactly:
 * supervisor enters the NEW ABSOLUTE running total from the conveyor scale.
 * Server computes the delta, records it, and it drives the flow rate.
 */
export default function LogSiloTonsModal({ exportOp, defaultSiloNo, onClose, onSuccess }) {
    const [siloNo, setSiloNo] = useState(defaultSiloNo);
    const [newTotal, setNewTotal] = useState("");
    const [busy, setBusy] = useState(false);

    // Compute the previous running total for the selected silo (within this export)
    const readings = exportOp.silo_tons_readings || [];
    const prevTotal = useMemo(() => {
        let prev = 0;
        for (const r of readings) if (r.silo_no === siloNo) prev = Number(r.new_total || 0);
        return prev;
    }, [readings, siloNo]);

    const parsedTotal = Number(newTotal) || 0;
    const delta = parsedTotal - prevTotal;
    const validDelta = newTotal !== "" && delta > 0;

    // Silo history for this export (all readings, most recent last)
    const history = useMemo(() => {
        return [...readings].filter((r) => r.silo_no === siloNo).sort((a, b) => (a.at > b.at ? -1 : 1));
    }, [readings, siloNo]);

    // Silos this export has used
    const silosUsed = useMemo(() => {
        const set = new Set([defaultSiloNo]);
        for (const s of exportOp.silo_segments || []) if (s.silo_no) set.add(s.silo_no);
        return Array.from(set).sort((a, b) => a - b);
    }, [exportOp, defaultSiloNo]);

    const submit = async (e) => {
        e.preventDefault();
        if (!validDelta) return toast.error(`New total must be greater than the previous silo-tons reading (${prevTotal.toLocaleString()} t).`);
        setBusy(true);
        try {
            await api.post(`/tronox/exports/${exportOp.id}/log-silo-tons`, {
                silo_no: siloNo,
                new_total: parsedTotal,
            });
            toast.success(`Silo ${siloNo}: +${delta.toLocaleString()} t · running total ${parsedTotal.toLocaleString()} t`);
            onSuccess();
        } catch (e2) { toast.error(formatApiError(e2)); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-3" data-testid="log-silo-tons-modal">
            <div className="bg-slate-900 text-slate-100 rounded-xl w-full max-w-md border border-slate-700 shadow-2xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/50 flex items-center justify-center">
                            <ChartLineUp size={18} weight="fill" className="text-amber-300" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-slate-400">Conveyor scale</div>
                            <div className="text-lg font-black">Log Silo Tons</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1" data-testid="close-log-silo-tons">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="p-4 space-y-4 overflow-y-auto">
                    <div>
                        <label className="text-[11px] uppercase text-slate-400 tracking-wider">Silo (source)</label>
                        <div className="grid grid-cols-4 gap-1.5 mt-1">
                            {silosUsed.map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setSiloNo(n)}
                                    className={`py-2 rounded text-sm font-black transition-colors ${siloNo === n ? "bg-teal-600 text-white ring-2 ring-teal-300" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
                                    data-testid={`silo-tons-pick-${n}`}
                                >
                                    #{n}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block">
                            <span className="text-[11px] uppercase text-slate-400 tracking-wider block">New conveyor total (running)</span>
                            <span className="text-[11px] text-slate-500 block mt-0.5">
                                Currently <b className="text-slate-300 font-mono">{prevTotal.toLocaleString()} t</b> for Silo {siloNo}
                            </span>
                        </label>
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            value={newTotal}
                            onChange={(e) => setNewTotal(e.target.value)}
                            placeholder={`e.g. ${(prevTotal + 500).toLocaleString()}`}
                            className="g-input mt-1 text-2xl font-mono font-black text-center"
                            data-testid="silo-tons-new-total-input"
                            autoFocus
                        />
                        {newTotal !== "" && delta <= 0 && (
                            <div className="text-[11px] text-rose-400 mt-1 font-semibold" data-testid="silo-tons-delta-warning">
                                New total must be greater than the previous reading ({prevTotal.toLocaleString()} t).
                            </div>
                        )}
                        {newTotal !== "" && delta > 0 && (
                            <div className="text-[11px] text-emerald-300 mt-1 font-mono" data-testid="silo-tons-delta-hint">
                                +{delta.toLocaleString()} t will be recorded ({prevTotal.toLocaleString()} → {parsedTotal.toLocaleString()}) · drives the flow rate.
                            </div>
                        )}
                    </div>

                    {history.length > 0 && (
                        <div className="border border-slate-700 rounded-lg overflow-hidden">
                            <div className="px-3 py-1.5 bg-slate-800/60 text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                                <ClockCounterClockwise size={11} /> Silo {siloNo} readings this export
                            </div>
                            <div className="divide-y divide-slate-800 max-h-40 overflow-y-auto">
                                {history.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`silo-tons-history-${r.id}`}>
                                        <span className="text-slate-500 font-mono">{new Date(r.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                                        <span className="text-slate-300">{Number(r.new_total).toLocaleString()} t total</span>
                                        <span className="text-emerald-400 font-bold">+{Number(r.delta_tons).toLocaleString()} t</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-2 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold" data-testid="silo-tons-cancel">Cancel</button>
                        <button type="submit" disabled={busy || !validDelta} className="flex-1 inline-flex items-center justify-center gap-2 py-2 rounded bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 text-white font-black" data-testid="silo-tons-submit">
                            <Check size={16} weight="bold" /> {busy ? "Saving…" : validDelta ? `+${delta.toLocaleString()} t` : "Log tons"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
